import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import PaymentTypeService from '../../services/paymentType.service';
import { useTranslation } from '../../hooks/useTranslation';

const PaymentTypeForm = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    monto: '',
    mora: '',
    dias_mora: '',
    cuotas: '',
    indefinite: false,
    by_Date: false,
    frecuencia: 'mensual',
    recurrencia: '',
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  useEffect(() => {
    if (isEdit) {
      fetchPaymentType();
    }
  }, [id, isEdit]);

  const fetchPaymentType = async () => {
    setLoading(true);
    try {
      const response = await PaymentTypeService.get(id);
      const pt = response.data?.data;
      setFormData({
        name: pt.name || '',
        monto: pt.monto || '',
        mora: pt.mora || '',
        dias_mora: pt.dias_mora || '',
        cuotas: pt.indefinite ? 0 : (pt.cuotas ?? ''),
        indefinite: Boolean(pt.indefinite),
        by_Date: pt.by_Date || false,
        frecuencia: pt.frecuencia || 'mensual',
        recurrencia: pt.recurrencia || '',
      });
    } catch (error) {
      console.error('Error loading payment type:', error);
      showSnackbar(t('paymentTypes.errorLoadingPaymentType'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (e) => {
    setFormData(prev => ({ ...prev, by_Date: e.target.checked }));
  };

  const handleIndefiniteChange = (e) => {
    const checked = e.target.checked;
    setFormData(prev => ({ ...prev, indefinite: checked, cuotas: checked ? 0 : prev.cuotas }));
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.monto) {
      showSnackbar(t('paymentTypes.pleaseFillRequired'), 'error');
      return;
    }

    const payload = {
      ...formData,
      cuotas: formData.indefinite ? 0 : (Number(formData.cuotas) || 0),
      indefinite: formData.indefinite,
    };
    try {
      setLoading(true);
      if (isEdit) {
        await PaymentTypeService.update(id, payload);
        showSnackbar(t('paymentTypes.updatedSuccess'));
      } else {
        await PaymentTypeService.create(payload);
        showSnackbar(t('paymentTypes.createdSuccess'));
      }
      navigate('/payment-types');
    } catch (error) {
      const errorMessage = error.response?.data?.message ||
                          (error.response?.data?.errors ?
                            Object.values(error.response.data.errors).flat().join(', ') :
                            t('paymentTypes.errorSaving'));
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  if (loading && isEdit) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardHeader
        title={
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={() => navigate('/payment-types')}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5">
              {isEdit ? t('paymentTypes.editPaymentType') : t('paymentTypes.newPaymentType')}
            </Typography>
          </Box>
        }
        action={
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={loading}
          >
            {isEdit ? t('paymentTypes.updatePaymentType') : t('paymentTypes.savePaymentType')}
          </Button>
        }
      />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('common.name')}
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('paymentTypes.amount')}
              name="monto"
              type="number"
              value={formData.monto}
              onChange={handleInputChange}
              required
              inputProps={{ min: 0, step: '0.01' }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('paymentTypes.lateFee')}
              name="mora"
              type="number"
              value={formData.mora}
              onChange={handleInputChange}
              required
              inputProps={{ min: 0, step: '0.01' }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('paymentTypes.lateFeeDays')}
              name="dias_mora"
              type="number"
              value={formData.dias_mora}
              onChange={handleInputChange}
              required
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.indefinite}
                  onChange={handleIndefiniteChange}
                />
              }
              label={t('paymentTypes.indefinite')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('paymentTypes.installments')}
              name="cuotas"
              type="number"
              value={formData.indefinite ? 0 : formData.cuotas}
              onChange={handleInputChange}
              required={!formData.indefinite}
              disabled={formData.indefinite}
              inputProps={{ min: 0 }}
              helperText={formData.indefinite ? t('paymentTypes.indefiniteHelp') : null}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('paymentTypes.frequency')}</InputLabel>
              <Select
                name="frecuencia"
                value={formData.frecuencia}
                onChange={handleInputChange}
                label={t('paymentTypes.frequency')}
              >
                <MenuItem value="mensual">{t('paymentTypes.monthly')}</MenuItem>
                <MenuItem value="anual">{t('paymentTypes.annual')}</MenuItem>
                <MenuItem value="diario">{t('paymentTypes.daily')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('paymentTypes.recurrence')}
              name="recurrencia"
              type="number"
              value={formData.recurrencia}
              onChange={handleInputChange}
              required
              inputProps={{ min: 1 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.by_Date}
                  onChange={handleSwitchChange}
                />
              }
              label={t('paymentTypes.byDate')}
            />
          </Grid>
        </Grid>
      </CardContent>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default PaymentTypeForm;
